import { useState, type ReactNode } from "react";

import type { Poll2Tier } from "./poll2Helpers";
import TeamFeedbackAccountCard from "./TeamFeedbackAccountCard";
import CheckInPollAccordion from "../MyCheckIns/CheckInPollAccordion";

const SAMPLE_CYCLE_ID = "sample-poll2-cycle";
const SAMPLE_PERIOD = "2026-Q3";

const SAMPLE_CYCLE = {
  id: SAMPLE_CYCLE_ID,
  name: "2026-Q3 · Poll 2 — Team Feedback",
  period: SAMPLE_PERIOD,
  due_date: "2026-09-30",
};

const noop = (): void => undefined;

const accountGridClass =
  "grid grid-cols-[repeat(auto-fill,minmax(280px,1fr))] gap-3.5";

export const TSF_SAMPLE_MESSAGES = {
  assigned:
    "Preview of assigned sponsor feedback — your program lead will launch Poll 2 from ExecOps Discovery Hub.",
  completed:
    "Preview of completed feedback — scores appear here after you submit Poll 2 for each account.",
} as const;

const SAMPLE_ASSIGNED = [
  {
    account_id: "sample-t1",
    account_name: "Northwind Logistics",
    sponsor_name: "Alex Kim",
    sponsor_role: "primary" as const,
  },
  {
    account_id: "sample-t2",
    account_name: "Summit Health Co.",
    sponsor_name: "Maria Santos",
    sponsor_role: "secondary" as const,
  },
  {
    account_id: "sample-t3",
    account_name: "Brightline Retail",
    sponsor_name: "James Tang",
    sponsor_role: "primary" as const,
  },
];

const SAMPLE_COMPLETED = [
  {
    account_id: "sample-t1",
    account_name: "Northwind Logistics",
    sponsor_name: "Alex Kim",
    sponsor_role: "primary" as const,
    team_feedback_received: true,
    poll2_raw: 12,
    poll2_dimensions: { involvement: 4, relationship: 4, knowledge: 4 },
    tier: "Good",
  },
  {
    account_id: "sample-t2",
    account_name: "Summit Health Co.",
    sponsor_name: "Maria Santos",
    sponsor_role: "secondary" as const,
    team_feedback_received: true,
    poll2_raw: 14,
    poll2_dimensions: { involvement: 5, relationship: 5, knowledge: 4 },
    tier: "Excellent",
  },
  {
    account_id: "sample-t3",
    account_name: "Brightline Retail",
    sponsor_name: "James Tang",
    sponsor_role: "primary" as const,
    team_feedback_received: true,
    poll2_raw: 11,
    poll2_dimensions: { involvement: 4, relationship: 4, knowledge: 3 },
    tier: "Good",
  },
];

const SamplePoll2Accordion = ({
  done,
  total,
  children,
}: {
  done: number;
  total: number;
  children: ReactNode;
}) => {
  const [expanded, setExpanded] = useState(true);
  return (
    <CheckInPollAccordion
      pollName="2026-Q3 · Poll 2 — Team Feedback"
      period={SAMPLE_PERIOD}
      cycle={SAMPLE_CYCLE}
      accountCount={total}
      poll1Done={done}
      poll1Total={total}
      progressLabel="Poll 2 progress"
      isExpanded={expanded}
      onToggleExpand={() => setExpanded(v => !v)}
      showPublicationBadge={false}
    >
      {children}
    </CheckInPollAccordion>
  );
};

type SampleProps = {
  poll2Tiers: Poll2Tier[];
};

export const SampleTeamFeedbackAssigned = ({ poll2Tiers }: SampleProps) => (
  <div className="flex flex-col gap-5 mt-1">
    <SamplePoll2Accordion done={0} total={3}>
      <div className={accountGridClass}>
        {SAMPLE_ASSIGNED.map(row => (
          <TeamFeedbackAccountCard
            key={row.account_id}
            account={row}
            isDraft={row.account_id === "sample-t2"}
            poll2Tiers={poll2Tiers}
            showStatusBadge="none"
            onAction={noop}
          />
        ))}
      </div>
    </SamplePoll2Accordion>
  </div>
);

export const SampleTeamFeedbackCompleted = ({ poll2Tiers }: SampleProps) => (
  <div className="flex flex-col gap-5 mt-1">
    <SamplePoll2Accordion done={3} total={3}>
      <div className={accountGridClass}>
        {SAMPLE_COMPLETED.map(row => (
          <TeamFeedbackAccountCard
            key={row.account_id}
            account={row}
            isDraft={false}
            poll2Tiers={poll2Tiers}
            showStatusBadge="none"
            onAction={noop}
          />
        ))}
      </div>
    </SamplePoll2Accordion>
  </div>
);
